import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { User } from '../models/user';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private static readonly USER_KEY = 'user';

  private user: User | null = null;

  constructor(private readonly httpClient: HttpClient) {}

  get isLoggedIn() {
    return !!this.getCurrentUser();
  }

  getCurrentUser() {
    if (this.user == null) {
      // Auth lives in an httpOnly cookie set by the server; what is stored
      // here is only the user profile (no token), so localStorage is safe and
      // the session is shared across tabs.
      const user = localStorage.getItem(SessionService.USER_KEY);

      if (!user) return null;

      try {
        this.user = JSON.parse(user);
      } catch (e) {
        return null;
      }
    }

    return this.user;
  }

  login(email: string, password: string) {
    return this.httpClient
      .post<User>(`${environment.baseurl}/api/auth/login`, { email, password }, { withCredentials: true })
      .pipe(
        tap((res) => {
          if (res && res.role === 'ADMIN') {
            this.setUser(res);
          }
        }),
      );
  }

  /** Trades the refresh-token cookie for a fresh access-token cookie. */
  refresh() {
    return this.httpClient
      .post<User>(`${environment.baseurl}/api/auth/refresh`, {}, { withCredentials: true })
      .pipe(tap((res) => this.setUser(res)));
  }

  logout() {
    // revoke the refresh token and clear the cookies server-side; the local
    // session is cleared regardless of whether the call succeeds
    this.httpClient
      .post(`${environment.baseurl}/api/auth/logout`, {}, { withCredentials: true })
      .subscribe({ error: () => {} });

    this.setUser(null);
  }

  private setUser(user: User | null) {
    if (!user) {
      localStorage.removeItem(SessionService.USER_KEY);
      this.user = null;
      return;
    }

    // never persist a token, even if the server sends one
    const { token, ...profile } = user;
    localStorage.setItem(SessionService.USER_KEY, JSON.stringify(profile));
    this.user = profile as User;
  }
}
